import styled from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../common/formInputsV2/styles/tokens';

// Same guarantee-card visual language as HouseProduct's GaranzieStep.styles.js
// and AncillariAutoProduct's AncillariStepTwo.styles.js — restyled checkbox,
// resting/hover/selected/locked border states. Uses v2Colors directly
// (unlike those two, which inline a local palette copy) because this
// stylesheet has a single owner, GuaranteeCommercialCard.jsx, imported by
// both the customer and BackOffice Commercial flows — no duplicate
// stylesheet to keep in sync.
const lockedSurface = '#f4f5f7';
const lockedBorder = 'rgba(16, 24, 40, 0.12)';
const labelResting = '#1d2939';
const lockedLabel = '#475467';
const primaryRing = 'rgba(0, 166, 81, 0.16)';

export const GuaranteeCheckboxItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
  padding: 14px 16px;
  background: ${v2Colors.inputBackground};
  border-radius: 14px;
  border: 1.5px solid ${(props) => {
    if (props.hasError) return v2Colors.danger;
    if (props.hasSuccess) return v2Colors.primary;
    return v2Colors.border;
  }};
  transition: border-color 120ms ease, box-shadow 120ms ease;

  /* Hover only when the row is actually toggleable */
  &:hover:not(:has(input:disabled)) {
    border-color: ${(props) => (props.hasError ? v2Colors.danger : v2Colors.borderStrong)};
  }

  /* Selected by the user: card stays white, crisp accent border + faint ring. */
  &:has(input:checked) {
    border-color: ${(props) => (props.hasError ? v2Colors.danger : v2Colors.primary)};
    box-shadow: 0 0 0 3px ${(props) => (props.hasError ? 'transparent' : primaryRing)};
  }

  /* Locked on (obbligatoria, e.g. RCT): softer accent, subtle grey fill. */
  &:has(input:checked:disabled) {
    background: ${lockedSurface};
    border-color: ${(props) => (props.hasError ? v2Colors.danger : lockedBorder)};
    box-shadow: none;
  }

  @media only screen and (max-width: ${v2Breakpoints.tablet}) {
    padding: 12px 13px;
  }
`;

export const GuaranteeCheckboxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

export const GuaranteeCheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;

  /* Custom checkbox — native control restyled in place (no markup change).
     Matches the v2 SelectableOption indicator: 20px, 6px radius, accent fill
     + drawn tick when checked. */
  input[type='checkbox'] {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin: 1px 0 0;
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1.5px solid ${v2Colors.border};
    border-radius: 6px;
    background: ${v2Colors.inputBackground};
    cursor: pointer;
    transition: background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;

    &::after {
      content: '';
      width: 5px;
      height: 9px;
      margin-top: -1px;
      border: solid ${v2Colors.inputBackground};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      opacity: 0;
      transition: opacity 120ms ease;
    }

    &:hover:not(:disabled) {
      box-shadow: 0 0 0 4px rgba(102, 112, 133, 0.16);
    }

    &:checked {
      background: ${v2Colors.primary};
      border-color: ${v2Colors.primary};
    }

    &:checked::after {
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid ${v2Colors.primary};
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  label {
    flex: 1;
    margin: 0;
    cursor: pointer;
    font-family: ${v2FontFamily};
    font-size: 15px;
    font-weight: 400;
    color: ${labelResting};
    line-height: 1.4;

    @media only screen and (max-width: ${v2Breakpoints.tablet}) {
      font-size: 14px;
    }
  }

  /* Typographic hierarchy:
     unselected  -> resting weight, dark slate
     user-picked -> bold, near-black
     locked on   -> settled: medium weight, muted grey (not competing) */
  input:checked + label {
    font-weight: 600;
    color: ${v2Colors.text};
  }

  input:checked:disabled + label {
    font-weight: 500;
    color: ${lockedLabel};
  }
`;

export const GuaranteeParameterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  padding-left: 28px;
  border-top: 1px solid rgba(16, 24, 40, 0.08);

  label {
    font-family: ${v2FontFamily};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: ${v2Colors.textMuted};
  }

  @media only screen and (max-width: ${v2Breakpoints.tablet}) {
    padding-left: 0;
  }
`;
